'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { PricingTier } from './PricingCards';
import { ServiceCategory } from './ServicesTabs';
import { createClient } from '@/lib/supabase/client';

interface OrderFormProps {
  selectedTier: PricingTier | null;
  category: ServiceCategory;
  isTiktok?: boolean;
  isInstagram?: boolean;
  isYoutube?: boolean;
}

const categoryLabels: Record<ServiceCategory, string> = {
  followers: 'Abonnés Facebook',
  likes: 'Likes Facebook',
  comments: 'Commentaires Facebook',
  views: 'Vues Facebook',
  tiktok_followers: 'Abonnés TikTok',
  tiktok_likes: 'Likes TikTok',
  tiktok_views: 'Vues TikTok',
  tiktok_comments: 'Commentaires TikTok',
  instagram_followers: 'Abonnés Instagram',
  instagram_likes: 'Likes Instagram',
  instagram_comments: 'Commentaires Instagram',
  instagram_views: 'Vues Instagram',
  youtube_followers: 'Abonnés YouTube',
  youtube_likes: 'Likes YouTube',
  youtube_views: 'Vues YouTube',
  youtube_comments: 'Commentaires YouTube',
};

const WHATSAPP_NUMBER = '2250720029251';

function buildWhatsAppMessage(serviceName: string, qty: string, price: number, profileUrl: string, isTiktok: boolean, isInstagram: boolean, isYoutube: boolean): string {
  const platform = isYoutube ? 'YouTube' : isInstagram ? 'Instagram' : isTiktok ? 'TikTok' : 'Facebook';
  const lines = [
    `🛒 *Nouvelle commande SocialBoost CI*`,
    ``,
    `📦 *Service :* ${serviceName}`,
    `🔢 *Quantité :* ${qty}`,
    `💰 *Prix :* ${price.toLocaleString('fr-CI')} XOF`,
    `🔗 *Lien ${platform} :* ${profileUrl}`,
    ``,
    `💳 *Paiement Mobile Money :*`,
    `🟠 Orange Money : 0720029251`,
    `🟡 MTN MoMo : 0575956671`,
    `🔵 Wave : 0575956671`,
    ``,
    `➡️ Envoyez le montant exact puis confirmez ici avec votre reçu.`,
  ];
  return lines.join('\n');
}

export default function OrderForm({ selectedTier, category, isTiktok = false, isInstagram = false, isYoutube = false }: OrderFormProps) {
  const [profileUrl, setProfileUrl] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderSent, setOrderSent] = useState(false);

  const platform = isYoutube ? 'YouTube' : isInstagram ? 'Instagram' : isTiktok ? 'TikTok' : 'Facebook';
  const isDark = isTiktok || isInstagram;

  const accentColor = isYoutube ? '#FF0000' : isInstagram ? '#C13584' : isTiktok ? '#2563EB' : '#1877F2';
  const accentGradient = isYoutube
    ? 'linear-gradient(135deg,#CC0000,#FF0000)'
    : isInstagram
    ? 'linear-gradient(135deg,#833AB4,#FD1D1D,#F77737)'
    : isTiktok
    ? 'linear-gradient(135deg,#010101,#2563EB)'
    : 'linear-gradient(135deg,#1877F2,#1565C0)';

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!profileUrl.trim()) errs.profileUrl = `Le lien ${platform} est requis.`;
    else if (!profileUrl.startsWith('https://')) errs.profileUrl = 'Le lien doit commencer par https://';
    if (!email.trim()) errs.email = "L'email est requis.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Email invalide.';
    if (!selectedTier) errs.tier = 'Veuillez sélectionner un pack.';
    return errs;
  };

  const insertOrder = async () => {
    try {
      const supabase = createClient();
      if (!supabase) return;
      await supabase.from('Orders').insert({
        customer_name: email.split('@')[0],
        customer_email: email,
        facebook_url: profileUrl,
        service_type: categoryLabels[category],
        quantity: selectedTier?.qtyNum,
        amount: selectedTier?.price,
        payment_method: 'Mobile Money (WhatsApp)',
        status: 'pending',
        'Total price': selectedTier?.price,
        products: [{ name: categoryLabels[category], qty: selectedTier?.qty, price: selectedTier?.price }],
      });
    } catch {
      // Silent fail
    }
  };

  const handleWhatsApp = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0 || !selectedTier) return;

    const message = buildWhatsAppMessage(
      categoryLabels[category],
      selectedTier.qty,
      selectedTier.price,
      profileUrl,
      isTiktok,
      isInstagram,
      isYoutube
    );
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');

    setLoading(true);
    await insertOrder();
    setLoading(false);
    setOrderSent(true);
  };

  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.08)', borderColor: `${accentColor}4D`, color: 'white' }
    : {};

  return (
    <div
      className="rounded-3xl p-6 sm:p-8 shadow-card"
      id="order-form"
      style={{
        background: isDark ? 'rgba(255,255,255,0.06)' : 'white',
        border: isDark ? `1px solid ${accentColor}40` : '1px solid rgba(13,27,62,0.06)',
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(37,211,102,0.12)' }}>
          <span className="text-xl">💬</span>
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg" style={{ color: isDark ? 'white' : '#0D1B3E' }}>Commander sur WhatsApp</h3>
          <p className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}>Rapide · Sécurisé · Mobile Money</p>
        </div>
      </div>

      {/* Selected tier summary */}
      {selectedTier ? (
        <div
          className="flex items-center justify-between p-4 rounded-2xl mb-6"
          style={{ background: `${accentColor}11`, border: `1.5px solid ${accentColor}33` }}
        >
          <div>
            <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: accentColor }}>{categoryLabels[category]}</div>
            <div className="font-heading font-bold" style={{ color: isDark ? 'white' : '#0D1B3E' }}>{selectedTier.qty}</div>
          </div>
          <div className="text-right">
            <div className="font-heading font-black text-xl" style={{ color: isDark ? 'white' : '#0D1B3E' }}>{selectedTier.price.toLocaleString('fr-CI')} XOF</div>
            <div className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}>Livraison : {selectedTier.delivery}</div>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center gap-3 p-4 rounded-2xl mb-6"
          style={{ background: 'rgba(244,169,35,0.06)', border: '1px solid rgba(244,169,35,0.2)' }}
        >
          <span className="text-xl">👆</span>
          <p className="text-sm text-[#92400E]">Sélectionnez un pack ci-dessus pour commencer.</p>
        </div>
      )}
      {errors.tier && <p className="text-red-500 text-xs mb-4">{errors.tier}</p>}

      <form onSubmit={handleWhatsApp} className="space-y-4">
        {/* Profile URL */}
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#374151' }}>
            Lien de votre{' '}
            {isYoutube
              ? 'chaîne / vidéo YouTube'
              : isInstagram
              ? 'profil Instagram'
              : isTiktok
              ? 'profil / vidéo TikTok' :'page / publication Facebook'}{' '}
            *
          </label>
          <input
            type="url"
            className="form-input"
            placeholder={
              isYoutube
                ? 'https://youtube.com/@votrechaîne'
                : isInstagram
                ? 'https://instagram.com/votrecompte'
                : isTiktok
                ? 'https://tiktok.com/@votrecompte'
                : 'https://facebook.com/votrepageoubien/posts/...'
            }
            value={profileUrl}
            onChange={(e) => setProfileUrl(e.target.value)}
            aria-label={`Lien ${platform}`}
            style={inputStyle}
          />
          {errors.profileUrl && <p className="text-red-500 text-xs mt-1">{errors.profileUrl}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#374151' }}>
            Email de confirmation *
          </label>
          <input
            type="email"
            className="form-input"
            placeholder="votre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email"
            style={inputStyle}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Payment info banner */}
        <div
          className="rounded-2xl p-4 space-y-2"
          style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.2)' }}
        >
          <p className="text-xs font-bold text-[#065F46] uppercase tracking-wide mb-2">💳 Paiement Mobile Money</p>
          <div className="flex items-center gap-2 text-sm text-[#374151]">
            <span>🟠</span>
            <span><strong>Orange Money :</strong> 0720029251</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#374151]">
            <span>🟡</span>
            <span><strong>MTN MoMo :</strong> 0575956671</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#374151]">
            <span>🔵</span>
            <span><strong>Wave :</strong> 0575956671</span>
          </div>
          <p className="text-xs text-[#6B7280] mt-2">Envoyez le montant exact puis confirmez avec votre reçu.</p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 text-base font-extrabold rounded-2xl flex items-center justify-center gap-3 transition-all"
          style={{
            background: selectedTier
              ? 'linear-gradient(135deg,#25D366 0%,#128C7E 100%)'
              : 'rgba(13,27,62,0.15)',
            color: selectedTier ? '#fff' : '#9CA3AF',
            fontFamily: 'Plus Jakarta Sans,sans-serif',
            cursor: selectedTier && !loading ? 'pointer' : 'not-allowed',
            boxShadow: selectedTier ? '0 4px 20px rgba(37,211,102,0.35)' : 'none',
            opacity: loading ? 0.8 : 1,
          }}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <span className="text-xl">💬</span>
              {selectedTier
                ? `Commander sur WhatsApp — ${selectedTier.price.toLocaleString('fr-CI')} XOF`
                : "Sélectionnez un pack d'abord"}
            </>
          )}
        </button>

        {orderSent && (
          <div
            className="flex items-center gap-2 p-3 rounded-xl text-sm"
            style={{ background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.25)', color: '#065F46' }}
          >
            <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-[#25D366] flex-shrink-0" />
            Commande enregistrée ! WhatsApp ouvert — complétez votre nom et adresse dans le message.
          </div>
        )}

        <p className="text-center text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#9CA3AF' }}>
          💬 Commande via WhatsApp · Paiement Mobile Money · Garantie 30 jours
        </p>
      </form>
    </div>
  );
}